import { type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Car, Building2, Users, LogOut, FileSpreadsheet } from 'lucide-react';
import { useDealerAuthStore } from '@/store/dealerAuthStore';

interface Props {
  children: ReactNode;
}

const NAV_ITEMS = [
  { to: '/dealer/dashboard',    icon: LayoutDashboard, labelKey: 'layout.dashboard' },
  { to: '/dealer/cars',         icon: Car,             labelKey: 'layout.myListings' },
  { to: '/dealer/bulk-import',  icon: FileSpreadsheet, labelKey: 'layout.bulkImport' },
  { to: '/dealer/company',      icon: Building2,        labelKey: 'layout.company',   ownerOnly: true },
  { to: '/dealer/team',         icon: Users,            labelKey: 'layout.team',      ownerOnly: true },
];

export function DealerLayout({ children }: Props) {
  const { t } = useTranslation('dealer');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, logout } = useDealerAuthStore();

  async function handleLogout() {
    await logout();
    navigate('/dealer/login');
  }

  const initials = user?.name
    ? user.name
        .split(' ')
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('')
    : '?';

  const isVerified = company?.status === 'verified';
  const isPending  = company?.status === 'pending';

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      <aside
        className="w-16 md:w-[260px] shrink-0 bg-white flex flex-col"
        style={{ boxShadow: '2px 0 12px 0 rgb(0 0 0 / 0.06)' }}
      >
        <div className="px-3 md:px-5 pt-6 pb-5 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-3 justify-center md:justify-start">
            <img
              src="/images/logo-kurdacars.png"
              alt="KurdaCars"
              className="w-10 h-10 rounded-xl object-cover shrink-0 select-none"
            />
            <div className="min-w-0 hidden md:block">
              <div className="font-semibold text-gray-900 text-sm leading-snug truncate">
                {company?.name ?? '—'}
              </div>
              <div className="text-xs text-gray-400 leading-snug">
                {t('layout.dealerPortal')}
              </div>
            </div>
          </div>

          {isVerified && (
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              {t('layout.verified', 'Verified')}
            </span>
          )}
          {isPending && (
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
              {t('layout.pending', 'Pending')}
            </span>
          )}
        </div>

        <nav className="flex-1 px-2 md:px-3 py-4 flex flex-col gap-0.5">
          {NAV_ITEMS.filter((item) => !item.ownerOnly || user?.role === 'owner').map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={[
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 relative',
                  active
                    ? 'bg-blue-50/80 text-blue-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                ].join(' ')}
              >
                {active && (
                  <span
                    className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-blue-500"
                    aria-hidden="true"
                  />
                )}
                <Icon
                  size={17}
                  strokeWidth={active ? 2.2 : 1.8}
                  className={active ? 'text-blue-600' : 'text-gray-400'}
                />
                <span className="hidden md:inline">{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-2 md:px-3 py-4 border-t border-gray-100">
          <div className="flex items-center gap-2.5 mb-3 px-1 justify-center md:justify-start">
            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center shrink-0 select-none">
              {initials}
            </div>
            <div className="min-w-0 flex-1 hidden md:block">
              <div className="text-sm font-medium text-gray-900 truncate leading-snug">
                {user?.name ?? '—'}
              </div>
              <span className="inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full leading-snug capitalize">
                {user?.role ?? 'seller'}
              </span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors duration-150"
          >
            <LogOut size={15} strokeWidth={1.8} />
            <span className="hidden md:inline">{t('layout.logout')}</span>
          </button>
        </div>
      </aside>

      <main
        className="flex-1 p-8 min-w-0"
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, rgba(219,234,254,0.2) 50%, rgba(224,231,255,0.15) 100%)',
        }}
      >
        {children}
      </main>
    </div>
  );
}

export default DealerLayout;
